// This import must remain first. The module removes a Gmail bearer attempt from
// window.location as it is evaluated, before the application/telemetry graph.
import {
  sanitizeTelemetryText,
  sanitizeTelemetryValue,
  sanitizeUrlForTelemetry,
} from './integrations/gmail/gmailAttemptVault';
import App, { initializeAppStore, store } from './App';
import { getAttributionTrackingId } from '@services/attribution/attribution';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import './index.css';
import ErrorBoundary from './ErrorBoundary';
import { PROTOCOL_VERSION } from '@services/user/user.saga';
import { markAppOpened } from '@helpers/appLifecycle';
import { getSelectedFamilyResource } from '@services/user/user.utils';
import { runCordovaPluginCall } from '@helpers/cordovaBridge';
import { bootstrapApp } from './appBootstrap';
import { logFirebaseAnalyticsEvent } from '@services/firebase/firebaseAnalytics';
import { installMetaAdvertiserTrackingSync } from '@services/meta/metaAdvertiserTracking';
import { createErrorReporter, ICrashlyticsStackFrame } from '@helpers/errorReporter';

declare let window: any;

interface ICapturedEventTarget {
  tagName?: string;
  id?: string;
  src?: string;
  href?: string;
  currentSrc?: string;
}

interface ICapturedErrorEvent {
  type?: string;
  message?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  isTrusted?: boolean;
}

type CordovaCallbackPhase = 'active' | 'completed' | 'threw';

interface ICordovaCallbackTrace {
  callbackId: string;
  plugin?: string;
  isSuccess: boolean;
  status?: number;
  statusName?: string;
  keepCallback: boolean;
  registered: boolean;
  argumentCount: number;
  argumentTypes: string[];
  startedAt: number;
  finishedAt?: number;
  phase: CordovaCallbackPhase;
}

interface ICordovaDocumentEventTrace {
  type: string;
  sync: boolean;
  startedAt: number;
  finishedAt?: number;
  phase: CordovaCallbackPhase;
}

const RECENT_CORDOVA_CALLBACK_WINDOW_MS = 5000;

let cordovaDeviceReadyFired = false;
let cordovaCallbackInstrumentationInstalled = false;
let cordovaDocumentEventInstrumentationInstalled = false;
let activeCordovaCallback: ICordovaCallbackTrace | undefined;
let lastCordovaCallback: ICordovaCallbackTrace | undefined;
let lastCordovaDocumentEvent: ICordovaDocumentEventTrace | undefined;

// Initialize before any render: a React boundary may report during startApp.
const IGNORED_ERROR_PATTERNS: RegExp[] = [
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /feature named pageObserver was not found/i,
  /AbortError/i,
  /The operation was aborted/i,
  /Request was aborted/i,
  // WebKit media-controls bug once a <video controls> element is collected (fixed upstream in 316507@main).
  /Can't find variable: EmptyRanges/,
  // Browser extensions can try to message a tab after it has closed.
  /Invalid call to runtime\.sendMessage\(\)\.\s*Tab not found\./i,
  // Brave iOS updates its wallet even when the provider was not injected (brave/brave-browser#58670).
  /undefined is not an object \(evaluating 'window\.ethereum\.selectedAddress = undefined'\)/,
];

// Safari reports browser-extension scripts under this scheme instead of their real URL.
const MASKED_SCRIPT_URL = /^webkit-masked-url:\/\//i;

// Only drop errors where every frame is masked: an extension wrapping an app API
// (e.g. fetch) can put a masked frame into an otherwise genuine app stack.
function isFromMaskedScript(stack: string, url?: string): boolean {
  const frames = parseStackFrames(stack, { url });
  return frames.length > 0 && frames.every((frame) => MASKED_SCRIPT_URL.test(frame.fileName));
}

// On the web every app script is same-origin, so its errors keep their message and
// location. A bare "Script error." there comes from code the browser or a host app's
// in-app browser injected. In Cordova it can still be a plugin, so keep reporting it.
function isOpaqueWebScriptError(message: unknown, url?: string, stack?: string): boolean {
  return !window.cordova
    && typeof message === 'string'
    && isLikelyScriptError(message)
    && !url
    && !stack;
}

// fetch() network-layer failures (Safari, Chrome, Firefox wording); Safari gives them no stack.
const NETWORK_FETCH_FAILURE_MESSAGE =
  /^(Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.)( \(.*\))?$/;

function isNetworkFetchFailure(input: any): boolean {
  return input?.name === 'TypeError'
    && typeof input.message === 'string'
    && NETWORK_FETCH_FAILURE_MESSAGE.test(input.message);
}

function shouldIgnoreError(input: any): boolean {
  if (isNetworkFetchFailure(input) || isNetworkFetchFailure(input?.error)) {
    return true;
  }

  const textParts = [
    typeof input === "string" ? input : "",
    typeof input?.name === "string" ? input.name : "",
    typeof input?.message === "string" ? input.message : "",
    typeof input?.stack === "string" ? input.stack : "",
    typeof input?.error?.name === "string" ? input.error.name : "",
    typeof input?.error?.message === "string" ? input.error.message : "",
    typeof input?.error?.stack === "string" ? input.error.stack : "",
  ].filter(Boolean);
  const text = textParts.join(" | ") || String(input ?? "");
  return IGNORED_ERROR_PATTERNS.some((re) => re.test(text));
}

function safeStringifyReason(reason: any): string {
  try {
    // Primitives
    if (reason == null) return String(reason); // "null"/"undefined"
    if (typeof reason === "string") return sanitizeTelemetryText(reason);
    if (typeof reason === "number" || typeof reason === "boolean" || typeof reason === "bigint") {
      return String(reason);
    }

    // Real Errors
    if (reason instanceof Error) {
      const headline = [reason.name, reason.message].filter(Boolean).join(": ") || reason.name || "Error";
      const stack = reason.stack ? `\n${reason.stack}` : "";
      return sanitizeTelemetryText(`${headline}${stack}`.trim());
    }

    // Error-like objects (message/stack but not instanceof Error)
    if (typeof reason === "object") {
      const name = safeGetString(reason, "name");
      const msg = safeGetString(reason, "message");
      const stack = safeGetString(reason, "stack");
      const headline = [name, msg].filter(Boolean).join(": ") || name || msg;
      if (headline || stack) return `${headline}${stack ? `\n${stack}` : ""}`.trim();

      // Snapshot own props safely, then stringify with cycle handling
      const snap = safeSnapshot(reason);
      return safeJSONStringify(snap);
    }

    // Functions / symbols
    return sanitizeTelemetryText(String(reason));
  } catch (e) {
    return `<<safeStringifyReason failed: ${sanitizeTelemetryText(e)}>>`;
  }
}

function safeGetString(obj: any, key: string): string {
  try {
    const v = obj?.[key];
    return typeof v === "string" ? sanitizeTelemetryText(v) : "";
  } catch {
    return "";
  }
}

function safeSnapshot(obj: any): any {
  const out: any = { _type: safeCtorName(obj) };

  let keys: string[] = [];
  try {
    keys = Object.getOwnPropertyNames(obj);
  } catch (e) {
    out._note = `getOwnPropertyNames threw: ${String(e)}`;
    return out;
  }

  for (const k of keys) {
    try {
      out[k] = sanitizeTelemetryValue(obj[k]);
    } catch (e) {
      out[k] = `<<getter threw: ${sanitizeTelemetryText(e)}>>`;
    }
  }
  return out;
}

function safeCtorName(obj: any): string {
  try {
    return obj?.constructor?.name || "Object";
  } catch {
    return "Object";
  }
}

function safeJSONStringify(value: any): string {
  try {
    const seen = new WeakSet();
    return JSON.stringify(sanitizeTelemetryValue(value), (k, v) => {
      if (typeof v === "object" && v !== null) {
        if (seen.has(v)) return "<<circular>>";
        seen.add(v);
      }
      return v;
    });
  } catch (e) {
    return `<<JSON stringify failed: ${sanitizeTelemetryText(e)}>>`;
  }
}

function compactObject<T extends Record<string, any>>(value: T): Partial<T> {
  const out: Partial<T> = {};

  Object.keys(value).forEach((key) => {
    const current = value[key];

    if (current === undefined || current === null || current === '') {
      return;
    }

    if (typeof current === 'number' && Number.isNaN(current)) {
      return;
    }

    if (Array.isArray(current) && current.length === 0) {
      return;
    }

    if (
      typeof current === 'object' &&
      !Array.isArray(current) &&
      Object.keys(current).length === 0
    ) {
      return;
    }

    (out as any)[key] = current;
  });

  return out;
}

function truncateString(value: string, maxLength = 240): string {
  if (!value || value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, Math.max(maxLength - 3, 0))}...`;
}

function toFiniteNumber(value: any): number | undefined {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function buildLocation(url?: string, lineNo?: number, colNo?: number): string {
  const safeUrl = sanitizeUrlForTelemetry(url || '(unknown)');
  const safeLine = toFiniteNumber(lineNo) ?? 0;
  const safeCol = toFiniteNumber(colNo) ?? 0;
  return `${safeUrl}:${safeLine}:${safeCol}`;
}

function buildErrorFingerprint(
  message: string,
  url?: string,
  lineNo?: number,
  colNo?: number,
  stack?: string
): string {
  return sanitizeTelemetryText([
    message || '',
    url || '',
    String(toFiniteNumber(lineNo) ?? 0),
    String(toFiniteNumber(colNo) ?? 0),
    truncateString(stack || '', 500),
  ].join('|'));
}

function describeEventTarget(target: EventTarget | null | undefined): ICapturedEventTarget | undefined {
  const element = target as any;
  if (!element || typeof element !== 'object') {
    return undefined;
  }

  const tagName = safeGetString(element, 'tagName');
  const described = compactObject({
    tagName: tagName ? tagName.toUpperCase() : undefined,
    id: safeGetString(element, 'id') || undefined,
    src: typeof element?.src === 'string' ? element.src : undefined,
    href: typeof element?.href === 'string' ? element.href : undefined,
    currentSrc: typeof element?.currentSrc === 'string' ? element.currentSrc : undefined,
  });

  return Object.keys(described).length > 0 ? described : undefined;
}

function getTargetUrl(target: EventTarget | null | undefined): string {
  const element = target as any;
  if (!element || typeof element !== 'object') {
    return '';
  }

  const candidates = [element?.src, element?.href, element?.currentSrc];
  const match = candidates.find((value) => typeof value === 'string' && value.length > 0);
  return typeof match === 'string' ? match : '';
}

function describeErrorEvent(event: any): ICapturedErrorEvent | undefined {
  if (!event || typeof event !== 'object') {
    return undefined;
  }

  const described = compactObject({
    type: typeof event.type === 'string' ? event.type : undefined,
    message: typeof event.message === 'string' ? event.message : undefined,
    filename: typeof event.filename === 'string' ? event.filename : undefined,
    lineno: toFiniteNumber(event.lineno),
    colno: toFiniteNumber(event.colno),
    isTrusted: typeof event.isTrusted === 'boolean' ? event.isTrusted : undefined,
  });

  return Object.keys(described).length > 0 ? described : undefined;
}

// Crawlers, link-preview fetchers and headless scrapers often abort stylesheet loads.
function isAutomatedClient(): boolean {
  const userAgent = window.navigator?.userAgent || '';
  return window.navigator?.webdriver === true
    || /^["']/.test(userAgent)
    || /bot|crawler|spider|slurp|headless|lighthouse|facebookexternalhit|preview/i.test(userAgent);
}

function shouldIgnoreResourceLoadError(
  event: Event,
  target?: ICapturedEventTarget
): boolean {
  const isAutomatedLink = target?.tagName === 'LINK' && isAutomatedClient();

  if (target?.tagName !== 'IMG' && target?.tagName !== 'VIDEO' && !isAutomatedLink) {
    return false;
  }

  const errorEvent = event as any;
  const msg = typeof errorEvent?.message === 'string' ? errorEvent.message : '';

  return !errorEvent?.error && (!msg || /resource load error/i.test(msg));
}

function extractMessage(input: any, fallback = 'Unknown error'): string {
  if (typeof input === 'string' && input) {
    return input;
  }

  if (input instanceof Error) {
    return input.message || input.name || fallback;
  }

  const message = safeGetString(input, 'message');
  if (message) {
    return message;
  }

  const serialized = safeStringifyReason(input);
  return serialized || fallback;
}

function extractStack(input: any): string {
  if (typeof input?.stack === 'string' && input.stack) {
    return input.stack;
  }

  const maybeStack = safeGetString(input, 'stack');
  if (maybeStack) {
    return maybeStack;
  }

  return '';
}

function parseStackLine(line: string): ICrashlyticsStackFrame | null {
  const trimmed = line.trim();
  if (!trimmed || /^error$/i.test(trimmed)) {
    return null;
  }

  let match = trimmed.match(/^at\s+(.*?)\s+\((.*?):(\d+):(\d+)\)$/);
  if (match) {
    return {
      functionName: match[1] || '(anonymous)',
      fileName: match[2] || '(unknown file)',
      lineNumber: Number(match[3]) || 0,
      columnNumber: Number(match[4]) || 0,
      source: trimmed,
    };
  }

  match = trimmed.match(/^at\s+(.*?):(\d+):(\d+)$/);
  if (match) {
    return {
      functionName: '(anonymous)',
      fileName: match[1] || '(unknown file)',
      lineNumber: Number(match[2]) || 0,
      columnNumber: Number(match[3]) || 0,
      source: trimmed,
    };
  }

  match = trimmed.match(/^(.*?)@(.*?):(\d+):(\d+)$/);
  if (match) {
    return {
      functionName: match[1] || '(anonymous)',
      fileName: match[2] || '(unknown file)',
      lineNumber: Number(match[3]) || 0,
      columnNumber: Number(match[4]) || 0,
      source: trimmed,
    };
  }

  return null;
}

function parseStackFrames(
  stack: string,
  fallback?: { url?: string; lineNo?: number; colNo?: number }
): ICrashlyticsStackFrame[] {
  const frames = stack
    .split('\n')
    .map(parseStackLine)
    .filter((frame): frame is ICrashlyticsStackFrame => Boolean(frame))
    .slice(0, 50);

  if (frames.length > 0) {
    return frames;
  }

  if (fallback?.url || fallback?.lineNo || fallback?.colNo) {
    return [
      {
        functionName: '(global)',
        fileName: fallback?.url || '(unknown file)',
        lineNumber: toFiniteNumber(fallback?.lineNo) ?? 0,
        columnNumber: toFiniteNumber(fallback?.colNo) ?? 0,
        source: buildLocation(fallback?.url, fallback?.lineNo, fallback?.colNo),
      },
    ];
  }

  return [];
}

function isLikelyScriptError(message: string): boolean {
  return /^script error\.?$/i.test(message.trim());
}

// Read the logged-in account / selected family from the Redux store so every report
// is attributable. Wrapped defensively: errors can fire before the store hydrates.
function getSessionIdentifiers(): { accountId?: number; familyId?: number } {
  try {
    const state: any = store.getState();
    return {
      accountId: toFiniteNumber(state?.user?.session?.data?.id),
      familyId: toFiniteNumber(getSelectedFamilyResource(state?.user?.session)?.id),
    };
  } catch {
    return {};
  }
}

function getCordovaCallbackPlugin(callbackId: string): string | undefined {
  const plugin = callbackId.replace(/\d+$/, '');
  return plugin && plugin !== callbackId ? plugin : undefined;
}

function getCordovaCallbackStatusName(cordova: any, status: unknown): string | undefined {
  if (typeof status !== 'number' || !cordova?.callbackStatus) {
    return;
  }

  return Object.keys(cordova.callbackStatus)
    .find((key) => cordova.callbackStatus[key] === status);
}

function describeCordovaCallbackArgument(value: unknown): string {
  if (value === null) {
    return 'null';
  }
  if (Array.isArray(value)) {
    return `array(${value.length})`;
  }
  return typeof value;
}

function getRecentCordovaCallbackContext(): Record<string, unknown> | undefined {
  const trace = activeCordovaCallback || lastCordovaCallback;
  if (!trace) {
    return;
  }

  const now = Date.now();
  const ageMs = Math.max(0, now - (trace.finishedAt || trace.startedAt));
  if (trace !== activeCordovaCallback && ageMs > RECENT_CORDOVA_CALLBACK_WINDOW_MS) {
    return;
  }

  return compactObject({
    callbackId: trace.callbackId,
    plugin: trace.plugin,
    isSuccess: trace.isSuccess,
    status: trace.status,
    statusName: trace.statusName,
    keepCallback: trace.keepCallback,
    registered: trace.registered,
    argumentCount: trace.argumentCount,
    argumentTypes: trace.argumentTypes,
    phase: trace === activeCordovaCallback ? 'active' : trace.phase,
    durationMs: trace.finishedAt === undefined
      ? Math.max(0, now - trace.startedAt)
      : Math.max(0, trace.finishedAt - trace.startedAt),
    ageMs,
  });
}

// Cordova already catches exceptions thrown by native plugin callbacks and emits
// `cordovacallbackerror` with the original Error. Keep a small, payload-free
// breadcrumb around callbackFromNative so that event—and any stripped-down
// window "Script error" that follows it—can identify the responsible plugin.
function installCordovaCallbackInstrumentation() {
  const cordova = window.cordova;
  const originalCallbackFromNative = cordova?.callbackFromNative;

  if (
    cordovaCallbackInstrumentationInstalled
    || !cordova
    || typeof originalCallbackFromNative !== 'function'
  ) {
    return;
  }

  cordova.callbackFromNative = function (
    this: any,
    callbackIdInput: unknown,
    isSuccessInput: unknown,
    statusInput: unknown,
    argsInput: unknown,
    keepCallbackInput: unknown
  ) {
    const callbackId = String(callbackIdInput ?? '');

    // Cordova uses INVALID for commands that supplied no JS callbacks. These
    // cannot throw from application callback code and would only add noise.
    if (!callbackId || callbackId === 'INVALID') {
      return originalCallbackFromNative.call(
        this,
        callbackIdInput,
        isSuccessInput,
        statusInput,
        argsInput,
        keepCallbackInput
      );
    }

    const args = Array.isArray(argsInput) ? argsInput : [];
    const previousActiveCallback = activeCordovaCallback;
    const safeCallbackId = truncateString(callbackId, 120);
    const trace: ICordovaCallbackTrace = {
      callbackId: safeCallbackId,
      plugin: getCordovaCallbackPlugin(safeCallbackId),
      isSuccess: isSuccessInput === true,
      status: toFiniteNumber(statusInput),
      statusName: getCordovaCallbackStatusName(cordova, statusInput),
      keepCallback: keepCallbackInput === true,
      registered: !!cordova.callbacks?.[callbackId],
      argumentCount: args.length,
      argumentTypes: args.slice(0, 8).map(describeCordovaCallbackArgument),
      startedAt: Date.now(),
      phase: 'active',
    };

    activeCordovaCallback = trace;
    lastCordovaCallback = trace;

    try {
      const result = originalCallbackFromNative.call(
        this,
        callbackIdInput,
        isSuccessInput,
        statusInput,
        argsInput,
        keepCallbackInput
      );
      trace.phase = 'completed';
      return result;
    } catch (error) {
      trace.phase = 'threw';
      throw error;
    } finally {
      trace.finishedAt = Date.now();
      lastCordovaCallback = trace;
      activeCordovaCallback = previousActiveCallback;
    }
  };

  cordovaCallbackInstrumentationInstalled = true;
}

function getRecentCordovaDocumentEventContext(): Record<string, unknown> | undefined {
  const trace = lastCordovaDocumentEvent;
  if (!trace) {
    return;
  }

  const now = Date.now();
  const ageMs = Math.max(0, now - (trace.finishedAt || trace.startedAt));
  if (trace.phase !== 'active' && ageMs > RECENT_CORDOVA_CALLBACK_WINDOW_MS) {
    return;
  }

  return compactObject({
    type: trace.type,
    sync: trace.sync,
    phase: trace.phase,
    ageMs,
  });
}

// Native code fires lifecycle events through evalJs. Cordova dispatches 'pause'
// synchronously inside that injected script, so a throwing listener reaches
// window.onerror as a detail-less "Script error.". Catch it here, where the Error
// and its stack are still intact, and report it with the event name instead.
function installCordovaDocumentEventInstrumentation() {
  const cordova = window.cordova;
  const originalFireDocumentEvent = cordova?.fireDocumentEvent;

  if (
    cordovaDocumentEventInstrumentationInstalled
    || !cordova
    || typeof originalFireDocumentEvent !== 'function'
  ) {
    return;
  }

  cordova.fireDocumentEvent = function (
    this: any,
    typeInput: unknown,
    data: unknown,
    bNoDetach: unknown
  ) {
    const type = truncateString(String(typeInput ?? ''), 60);
    const trace: ICordovaDocumentEventTrace = {
      type,
      sync: !!bNoDetach,
      startedAt: Date.now(),
      phase: 'active',
    };
    lastCordovaDocumentEvent = trace;

    try {
      const result = originalFireDocumentEvent.call(this, typeInput, data, bNoDetach);
      trace.phase = 'completed';
      return result;
    } catch (error) {
      trace.phase = 'threw';
      // Rethrowing would only add the opaque duplicate this wrapper exists to replace.
      reportHandledError(`Cordova ${type || 'unknown'} event handler`, error, {
        documentEvent: type,
        sync: trace.sync,
      });
      return;
    } finally {
      trace.finishedAt = Date.now();
    }
  };

  cordovaDocumentEventInstrumentationInstalled = true;
}

function getViewportRuntimeContext(): Record<string, unknown> | undefined {
  try {
    const visualViewport = (window as any).visualViewport;
    const activeElement = document.activeElement;
    const activeElementTag = activeElement?.tagName?.toLowerCase();
    const inputType = activeElement instanceof HTMLInputElement
      ? activeElement.type
      : activeElement instanceof HTMLTextAreaElement
        ? "textarea"
        : activeElement instanceof HTMLElement && activeElement.isContentEditable
          ? "contenteditable"
          : undefined;

    return compactObject({
      innerWidth:toFiniteNumber(window.innerWidth),
      innerHeight:toFiniteNumber(window.innerHeight),
      scrollX:toFiniteNumber(window.scrollX),
      scrollY:toFiniteNumber(window.scrollY),
      orientation:(window.screen as any)?.orientation?.type,
      activeElement:compactObject({
        tag:activeElementTag,
        inputType,
      }),
      visualViewport:visualViewport
        ? compactObject({
          width:toFiniteNumber(visualViewport.width),
          height:toFiniteNumber(visualViewport.height),
          offsetLeft:toFiniteNumber(visualViewport.offsetLeft),
          offsetTop:toFiniteNumber(visualViewport.offsetTop),
          pageLeft:toFiniteNumber(visualViewport.pageLeft),
          pageTop:toFiniteNumber(visualViewport.pageTop),
          scale:toFiniteNumber(visualViewport.scale),
        })
        : undefined,
    });
  } catch {
    return;
  }
}

function buildRuntimeContext(extra: Record<string, unknown> = {}): Record<string, unknown> {
  const connection = (navigator as any)?.connection;
  const session = getSessionIdentifiers();

  return compactObject({
    accountId: session.accountId,
    familyId: session.familyId,
    href: sanitizeUrlForTelemetry(window.location?.href || ''),
    readyState: document.readyState,
    visibilityState: document.visibilityState,
    online: navigator.onLine,
    connectionType: typeof connection?.type === 'string' ? connection.type : undefined,
    userAgent: truncateString(navigator.userAgent || '', 240),
    cordova: compactObject({
      present: !!window.cordova,
      deviceReady: cordovaDeviceReadyFired,
      platformId: window.cordova?.platformId,
      version: window.cordova?.version,
    }),
    cordovaCallback: getRecentCordovaCallbackContext(),
    cordovaDocumentEvent: getRecentCordovaDocumentEventContext(),
    viewport: getViewportRuntimeContext(),
    device: compactObject({
      platform: window.device?.platform,
      version: window.device?.version,
      manufacturer: window.device?.manufacturer,
      model: window.device?.model,
      isVirtual: window.device?.isVirtual,
    }),
    extra: compactObject(extra),
  });
}

function formatErrorText(
  kind: string,
  message: string,
  location: string,
  stack: string,
  context: Record<string, unknown>,
  hint?: string
): string {
  const lines = [
    `${kind}: ${message} @ ${location}`,
    `Stack: ${stack || 'N/A'}`,
  ];

  if (hint) {
    lines.push(`Hint: ${hint}`);
  }

  lines.push(`Context: ${safeJSONStringify(context)}`);
  return sanitizeTelemetryText(lines.join('\n'));
}

function reportToNativeCrashlytics(errorText: string, stackFrames: ICrashlyticsStackFrame[]) {
  const FirebasePlugin = window.FirebasePlugin;

  if (!window.cordova || !FirebasePlugin?.logError) {
    return;
  }

  try {
    const safeErrorText = sanitizeTelemetryText(errorText);
    const safeStackFrames = sanitizeTelemetryValue(stackFrames) as ICrashlyticsStackFrame[];
    if (safeStackFrames.length > 0) {
      runCordovaPluginCall("FirebasePlugin.logError", () => FirebasePlugin.logError(truncateString(safeErrorText, 4000), safeStackFrames));
      return;
    }

    runCordovaPluginCall("FirebasePlugin.logError", () => FirebasePlugin.logError(truncateString(safeErrorText, 4000)));
  } catch (nativeReportError) {
    console.warn('Failed to forward error to Firebase Crashlytics:', sanitizeTelemetryValue(nativeReportError));
  }
}

const reportErrorOncePerSession = createErrorReporter(PROTOCOL_VERSION, reportToNativeCrashlytics);

// Native-invoked callbacks (push tap, deep links, share sheet) run outside React's
// ErrorBoundary; on iOS WKWebView a throw there reaches window.onerror stripped down
// to a detail-less "Script error.". Catching at the source keeps the real stack.
function reportHandledError(
  label: string,
  error: unknown,
  extra: Record<string, unknown> = {}
) {
  if (shouldIgnoreError(error) || shouldIgnoreError((error as any)?.message)) {
    return;
  }

  const msg = extractMessage(error, label);
  const stack = extractStack(error);
  const url = safeGetString(error, 'fileName');
  const lineNo = toFiniteNumber((error as any)?.lineNumber);
  const colNo = toFiniteNumber((error as any)?.columnNumber);
  const stackFrames = parseStackFrames(stack, { url, lineNo, colNo });
  const context = buildRuntimeContext({
    handler: 'handled',
    label,
    ...extra,
  });

  reportErrorOncePerSession(
    formatErrorText(label, msg, buildLocation(url, lineNo, colNo), stack, context),
    {
      fingerprint: buildErrorFingerprint(msg, url || label, lineNo, colNo, stack),
      stackFrames,
    }
  );
}

(window as any).reportHandledError = reportHandledError;

let facebookIOSBridgeShimReported = false;

function reportFacebookIOSBridgeShimUse() {
  if (facebookIOSBridgeShimReported || !(window as any).__vvFacebookIOSBridgeShimUsed) {
    return;
  }

  facebookIOSBridgeShimReported = true;

  const msg = 'Facebook iOS injected script used missing window.webkit.messageHandlers bridge';
  const context = buildRuntimeContext({
    handler: 'facebook-ios-bridge-shim',
    bridgeHandlers: (window as any).__vvFacebookIOSBridgeShimHandlers,
  });

  reportErrorOncePerSession(
    formatErrorText(
      'External browser bridge shim',
      msg,
      buildLocation(window.location?.href, 0, 0),
      '',
      context,
      'The page supplied a no-op WKWebView bridge so Facebook in-app browser telemetry could not throw an unhandled exception.'
    ),
    {
      fingerprint: buildErrorFingerprint(
        msg,
        'facebook-ios-bridge-shim',
        0,
        0,
        safeJSONStringify((window as any).__vvFacebookIOSBridgeShimHandlers || [])
      ),
    }
  );
}

(window as any).__vvReportFacebookIOSBridgeShimUse = reportFacebookIOSBridgeShimUse;
reportFacebookIOSBridgeShimUse();

// --- FELHANTERING OCH DEBUGGING ---

window.addEventListener('cordovacallbackerror', (event: any) => {
  console.error('--- 🚨 CORDOVA-FEL FÅNGAT (cordovacallbackerror) 🚨 ---');
  console.error('Meddelande:', sanitizeTelemetryValue(event?.error));

  const err = event?.error;
  const msg = extractMessage(err ?? event, 'Unknown Cordova callback error');
  const stack = extractStack(err);
  const url = safeGetString(err, 'fileName') || safeGetString(event, 'filename') || 'cordovacallbackerror';
  const lineNo = toFiniteNumber(err?.lineNumber) ?? toFiniteNumber(event?.lineno);
  const colNo = toFiniteNumber(err?.columnNumber) ?? toFiniteNumber(event?.colno);
  const stackFrames = parseStackFrames(stack, { url, lineNo, colNo });
  const context = buildRuntimeContext({
    handler: 'cordovacallbackerror',
    event: describeErrorEvent(event),
  });

  reportErrorOncePerSession(
    formatErrorText(
      'Cordova callback error',
      msg,
      buildLocation(url, lineNo, colNo),
      stack,
      context
    ),
    {
      fingerprint: buildErrorFingerprint(msg, url, lineNo, colNo, stack),
      stackFrames,
    }
  );

  // addEventListener ignores return value, but keeping it doesn't hurt
  return true;
});

window.addEventListener(
  'error',
  (event: Event) => {
    const errorEvent = event as any;
    const error = errorEvent?.error;
    const target = describeEventTarget(errorEvent?.target);
    const targetUrl = getTargetUrl(errorEvent?.target);
    const msg =
      typeof errorEvent?.message === 'string' && errorEvent.message
        ? errorEvent.message
        : target?.tagName
          ? `${target.tagName} resource load error`
          : 'Unknown global error event';

    if (
      shouldIgnoreResourceLoadError(event, target) ||
      shouldIgnoreError(msg) ||
      shouldIgnoreError(error) ||
      isFromMaskedScript(extractStack(error), safeGetString(errorEvent, 'filename')) ||
      isOpaqueWebScriptError(msg, safeGetString(errorEvent, 'filename') || targetUrl, extractStack(error))
    ) {
      return;
    }

    console.error('--- 🚨 GLOBAL ERROR EVENT FÅNGAT (window.addEventListener:error) 🚨 ---');
    console.error('Event:', sanitizeTelemetryValue(errorEvent));

    const url =
      safeGetString(errorEvent, 'filename') ||
      safeGetString(error, 'fileName') ||
      targetUrl ||
      '';
    const lineNo = toFiniteNumber(errorEvent?.lineno) ?? toFiniteNumber(error?.lineNumber);
    const colNo = toFiniteNumber(errorEvent?.colno) ?? toFiniteNumber(error?.columnNumber);
    const stack = extractStack(error);
    const hint =
      isLikelyScriptError(msg) && !stack
        ? 'Browser/WebView hid the original exception. Target and Cordova runtime context were attached to help identify the failing script or plugin.'
        : undefined;
    const stackFrames = parseStackFrames(stack, { url, lineNo, colNo });
    const context = buildRuntimeContext({
      handler: 'window.error',
      event: describeErrorEvent(errorEvent),
      target,
    });

    reportErrorOncePerSession(
      formatErrorText(
        'Global error event',
        msg,
        buildLocation(url, lineNo, colNo),
        stack,
        context,
        hint
      ),
      {
        fingerprint: buildErrorFingerprint(msg, url, lineNo, colNo, stack),
        stackFrames,
      }
    );
  },
  true
);

window.onerror = (
  message: string | Event,
  url: string,
  lineNo: number,
  colNo: number,
  error?: Error
) => {
  if (
    shouldIgnoreError(message) ||
    shouldIgnoreError(error) ||
    isFromMaskedScript(extractStack(error), url) ||
    isOpaqueWebScriptError(message, url, extractStack(error))
  ) {
    // stop reporting + prevent bubbling noise
    return true;
  }

  console.error('--- 🚨 GLOBAL SYNKRON-FEL FÅNGAT (window.onerror) 🚨 ---');
  console.error('Meddelande:', sanitizeTelemetryValue(message));

  const msg = extractMessage(message, 'Non-string error event');
  const stack = extractStack(error);
  const hint =
    isLikelyScriptError(msg) && !stack
      ? 'Browser/WebView hid the original exception. The separate error-event handler should include any available target and Cordova context.'
      : undefined;
  const stackFrames = parseStackFrames(stack, { url, lineNo, colNo });
  const context = buildRuntimeContext({
    handler: 'window.onerror',
  });

  reportErrorOncePerSession(
    formatErrorText(
      'Global sync error',
      msg,
      buildLocation(url, lineNo, colNo),
      stack,
      context,
      hint
    ),
    {
      fingerprint: buildErrorFingerprint(msg, url, lineNo, colNo, stack),
      stackFrames,
    }
  );

  return true;
};

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  if (shouldIgnoreError(event.reason) || isFromMaskedScript(extractStack(event.reason))) {
    event.preventDefault();
    return;
  }

  console.error('--- 🚨 GLOBAL ASYNKRON-FEL FÅNGAT (unhandledrejection) 🚨 ---');
  console.error('Orsak:', sanitizeTelemetryValue(event.reason));

  const msg = extractMessage(event.reason, 'Unhandled rejection');
  const stack = extractStack(event.reason);
  const stackFrames = parseStackFrames(stack);
  const context = buildRuntimeContext({
    handler: 'unhandledrejection',
  });

  reportErrorOncePerSession(
    formatErrorText(
      'Unhandled rejection',
      msg,
      buildLocation(window.location?.href, 0, 0),
      stack || safeStringifyReason(event.reason),
      context
    ),
    {
      fingerprint: buildErrorFingerprint(msg, 'unhandledrejection', 0, 0, stack),
      stackFrames,
    }
  );

  event.preventDefault();
});

// ------------------------------------

const startApp = (cordovaApp: boolean) => {
  markAppOpened();

  console.log(`Starting React App (Cordova Mode: ${cordovaApp})`);

  ReactDOM.render(
    <ErrorBoundary
      onError={(error, errorInfo) => reportHandledError('React render error', error, {
        componentStack: errorInfo.componentStack,
      })}
      fallback={<div>We encountered an error. Please report to hello@co-parenting.app</div>}
    >
      <App cordovaApp={cordovaApp} />
    </ErrorBoundary>,
    document.getElementById('root') as HTMLElement
  );
};

if (window.cordova) {
  console.log('Cordova detected. Waiting for deviceready...');
  installCordovaCallbackInstrumentation();
  installCordovaDocumentEventInstrumentation();
} else {
  console.log('Running in Browser mode.');
}

bootstrapApp({
  initializeStore: initializeAppStore,
  startApp,
  onDeviceReady: () => {
    cordovaDeviceReadyFired = true;
    installMetaAdvertiserTrackingSync();
    void getAttributionTrackingId();
  },
  onMigrationChecked: (outcome) => {
    // Only the outcome is reported; never include snapshot data or credentials.
    void logFirebaseAnalyticsEvent('native_origin_migration', { outcome })
      .catch(() => console.warn('Could not report the origin migration outcome.'));
  },
});



// WEBPACK FOOTER //
// ./src/index.tsx